import { zodResolver } from '@hookform/resolvers/zod';
import { FC, useEffect, useRef } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { submitFormByRef } from '../../../../utils';
import { SimpleButton } from '../../../common/Buttons';
import { useDirtyFormBlockNavigation } from '../../../common/Forms/NavigationBlocker';
import { Row, Visible } from '../../../common/LayoutComponents';
import { CombineSameContractWarning } from '../Common/CombineSameContractWarning';
import { SpecialDayMixedPrioritiesWarning } from '../Common/SpecialDayMixedPrioritiesWarning';
import { TimetableImportStrategyForm } from '../Common/TimetableImportStrategyForm';
import {
  FormState,
  timetablesImportFormSchema,
} from '../Common/TimetablesImportFormSchema';
import { TimetablesImportPriorityForm } from '../Common/TimetablesImportPriorityForm';

type ConfirmTimetablesImportFormProps = {
  readonly defaultValues?: Partial<FormState>;
  readonly className?: string;
  readonly combiningSameContractTimetables: boolean;
  readonly inconsistentSpecialDayPrioritiesStaged: boolean;
  readonly fetchStagingAndTargetFramesForCombine: (priority: number) => void;
  readonly clearStagingAndTargetFramesForCombine: () => void;
  readonly onSubmit: (state: FormState) => void;
  readonly onCancel: () => void;
};

const testIds = {
  saveButton: 'ConfirmTimetablesImportForm::saveButton',
  cancelButton: 'ConfirmTimetablesImportForm::cancelButton',
  strategyRadioButtonPrefix: 'ConfirmTimetablesImportForm',
};

export const ConfirmTimetablesImportForm: FC<
  ConfirmTimetablesImportFormProps
> = ({
  defaultValues,
  className,
  combiningSameContractTimetables,
  inconsistentSpecialDayPrioritiesStaged,
  fetchStagingAndTargetFramesForCombine,
  clearStagingAndTargetFramesForCombine,
  onSubmit,
  onCancel,
}) => {
  const { t } = useTranslation();
  const formRef = useRef<ExplicitAny>(null);

  const methods = useForm<FormState>({
    defaultValues,
    resolver: zodResolver(timetablesImportFormSchema),
  });
  useDirtyFormBlockNavigation(methods.formState, 'ConfirmTimetablesImportForm');

  const { handleSubmit, watch } = methods;

  const { priority, timetableImportStrategy } = watch();
  useEffect(() => {
    if (timetableImportStrategy === 'combine' && priority) {
      fetchStagingAndTargetFramesForCombine(priority);
    } else {
      clearStagingAndTargetFramesForCombine();
    }
  }, [
    fetchStagingAndTargetFramesForCombine,
    clearStagingAndTargetFramesForCombine,
    priority,
    timetableImportStrategy,
  ]);

  const onSave = () => {
    submitFormByRef(formRef);
  };

  return (
    <FormProvider {...methods}>
      <form
        id="confirm-timetables-import-form"
        className={className}
        onSubmit={handleSubmit(onSubmit)}
        ref={formRef}
      >
        <div className="space-y-7">
          <h3>
            {t(($) => $.confirmTimetablesImportModal.importStrategy.title)}
          </h3>
          <TimetableImportStrategyForm
            testIdPrefix={testIds.strategyRadioButtonPrefix}
          />

          <Visible visible={combiningSameContractTimetables}>
            <CombineSameContractWarning
              className="w-[480px]"
              iconClassName="h-12 w-12"
            />
          </Visible>

          <h3>{t(($) => $.confirmTimetablesImportModal.priority)}</h3>
          <TimetablesImportPriorityForm />

          <Visible visible={inconsistentSpecialDayPrioritiesStaged}>
            <SpecialDayMixedPrioritiesWarning />
          </Visible>

          <Row className="mt-10 justify-end gap-4">
            <SimpleButton
              testId={testIds.cancelButton}
              onClick={onCancel}
              inverted
            >
              {t(($) => $.cancel)}
            </SimpleButton>
            <SimpleButton testId={testIds.saveButton} onClick={onSave}>
              {t(($) => $.save)}
            </SimpleButton>
          </Row>
        </div>
      </form>
    </FormProvider>
  );
};
